/**
 * manager-header.js
 * Gerencia dropdowns de navegação desktop com hover/focus e delay.
 * O drawer mobile é gerenciado pelo DrawerManager (data-drawer="menu").
 * Disponível como window.HeaderManager.
 *
 * ─── HTML esperado ────────────────────────────────────────────────────────
 *
 *   <!-- Container (escopo dos triggers) -->
 *   <nav data-header>
 *
 *     <!-- Trigger: valor deve corresponder ao data-dropdown-menu -->
 *     <a href="/colecoes" data-dropdown-trigger="roupas">Roupas</a>
 *
 *   </nav>
 *
 *   <!-- Menu dropdown (em qualquer lugar do DOM) -->
 *   <div data-dropdown-menu="roupas" aria-hidden="true">
 *     <!-- Elemento interno: hover aqui mantém o menu aberto -->
 *     <div data-dropdown-content>
 *       ...
 *     </div>
 *   </div>
 *
 * ─── API pública ──────────────────────────────────────────────────────────
 *
 *   window.HeaderManager.closeDropdowns()
 *   window.HeaderManager.destroy()
 *
 * ─── Eventos emitidos via window.eventBus ─────────────────────────────────
 *
 *   'dropdown:open'  → { name, menu, trigger }
 *   'dropdown:close' → { name, menu }
 */

class HeaderManager {
  constructor(options = {}) {
    this._config = {
      containerSelector: '[data-header]',
      triggerSelector:   '[data-dropdown-trigger]',
      menuSelector:      '[data-dropdown-menu]',
      openDelay:  150,
      closeDelay: 200,
      openClass:  'is-open',
      activeClass: 'is-active',
      ...options,
    };

    /** @type {Map<string, ReturnType<typeof setTimeout>>} */
    this._timers = new Map();
    this._activeMenu = null;

    this._onKeyDown = this._onKeyDown.bind(this);
    this._onClick   = this._onClick.bind(this);
    this._onResize  = this._onResize.bind(this);
    this._onScroll  = this._onScroll.bind(this);
    this._onMobileMenuClick = this._onMobileMenuClick.bind(this);
    this._onHeaderMenuToggleClick = this._onHeaderMenuToggleClick.bind(this);
    this._onHeaderMenuClick = this._onHeaderMenuClick.bind(this);
    this._onHeaderMenuScroll = this._onHeaderMenuScroll.bind(this);

    this._init();
    this._initHeaderMenu();
    this._initMobileMenu();
    this._bindGlobalEvents();
  }

  // ─── Inicialização ────────────────────────────────────────────────────────

  _init() {
    const container = document.querySelector(this._config.containerSelector);
    if (!container) return;

    container.querySelectorAll(this._config.triggerSelector).forEach(trigger => {
      const name = trigger.dataset.dropdownTrigger;
      const menu = document.querySelector(`[data-dropdown-menu="${name}"]`);
      if (!menu) return;

      // Hover no trigger
      trigger.addEventListener('mouseenter', () => this._scheduleOpen(name));
      trigger.addEventListener('mouseleave', () => this._scheduleClose(name));

      // Hover na área interna (data-dropdown-content) mantém o menu aberto;
      // sem ele, o hover é mantido no menu inteiro.
      const hoverTarget = menu.querySelector('[data-dropdown-content]') || menu;
      hoverTarget.addEventListener('mouseenter', () => this._cancelClose(name));
      hoverTarget.addEventListener('mouseleave', () => this._scheduleClose(name));

      // Suporte a teclado (focus/blur)
      trigger.addEventListener('focus', () => this._scheduleOpen(name));
      trigger.addEventListener('blur',  () => this._scheduleClose(name));
    });

    // Fecha dropdowns ao abrir qualquer drawer
    window.eventBus?.on('drawer:open', () => this.closeDropdowns());
  }

  _initMobileMenu() {
    const drawer = document.querySelector('[data-drawer="menu"]');
    if (!drawer) return;

    this._mobileMenuDrawer = drawer;
    this._mobileMenuRoot = drawer.querySelector('[data-mobile-menu-panel="root"]');
    drawer.addEventListener('click', this._onMobileMenuClick);

    window.eventBus?.on('drawer:open', ({ name }) => {
      if (name === 'menu') this._showMobileMenuPanel('root');
    });
  }

  _initHeaderMenu() {
    const toggle = document.querySelector('[data-header-menu-toggle]');
    const panel = document.querySelector('[data-header-menu-panel]');
    if (!toggle || !panel) return;

    this._headerMenuToggle = toggle;
    this._headerMenuPanel = panel;
    toggle.addEventListener('click', this._onHeaderMenuToggleClick);
    panel.addEventListener('click', this._onHeaderMenuClick);
    this._headerMenuScrollContainers = Array.from(
      panel.querySelectorAll('[data-header-menu-root], [data-header-menu-submenu]')
    );
    this._headerMenuScrollContainers.forEach(scrollContainer => {
      scrollContainer.addEventListener('scroll', this._onHeaderMenuScroll, { passive: true });
    });
  }

  _onHeaderMenuScroll(event) {
    this._updateHeaderMenuScrollbar(event.currentTarget);
  }

  _updateHeaderMenuScrollbar(scrollContainer) {
    if (!scrollContainer) return;

    const trackHeight = Math.max(0, scrollContainer.clientHeight - 24);
    const isScrollable = scrollContainer.scrollHeight > scrollContainer.clientHeight + 1 && trackHeight > 0;
    scrollContainer.classList.toggle('is-scrollable', isScrollable);

    if (!isScrollable) return;

    const thumbHeight = Math.max(32, Math.round(trackHeight * scrollContainer.clientHeight / scrollContainer.scrollHeight));
    const maxThumbOffset = Math.max(0, trackHeight - thumbHeight);
    const scrollRange = Math.max(1, scrollContainer.scrollHeight - scrollContainer.clientHeight);
    const thumbOffset = Math.round((scrollContainer.scrollTop / scrollRange) * maxThumbOffset);
    scrollContainer.style.setProperty('--header-menu-scroll-thumb-height', `${thumbHeight}px`);
    scrollContainer.style.setProperty('--header-menu-scroll-thumb-offset', `${thumbOffset}px`);
  }

  _onHeaderMenuToggleClick(event) {
    event.preventDefault();

    if (this._headerMenuPanel?.classList.contains(this._config.openClass)) {
      this._closeHeaderMenu();
      return;
    }

    this._openHeaderMenu();
  }

  _onHeaderMenuClick(event) {
    const backButton = event.target.closest('[data-header-menu-back]');
    if (backButton && this._headerMenuPanel?.contains(backButton)) {
      event.preventDefault();
      this._showHeaderMenuRoot();
      return;
    }

    const item = event.target.closest('[data-header-menu-item]');
    if (!item || !this._headerMenuPanel?.contains(item)) return;

    event.preventDefault();
    this._activateHeaderMenuItem(item.dataset.headerMenuItem, true);
  }

  _openHeaderMenu() {
    const panel = this._headerMenuPanel;
    if (!panel) return;

    panel.classList.add(this._config.openClass);
    panel.setAttribute('aria-hidden', 'false');
    this._headerMenuToggle?.setAttribute('aria-expanded', 'true');

    this._showHeaderMenuRoot();
    requestAnimationFrame(() => this._positionHeaderMenu());
  }

  _activateHeaderMenuItem(name, showSubmenu = false) {
    const panel = this._headerMenuPanel;
    if (!panel || !name) return;

    const root = panel.querySelector('[data-header-menu-root]');
    if (root) root.hidden = showSubmenu;

    panel.querySelectorAll('[data-header-menu-item]').forEach(item => {
      const isActive = showSubmenu && item.dataset.headerMenuItem === name;
      item.classList.toggle(this._config.activeClass, isActive);
      item.setAttribute('aria-expanded', String(isActive));
    });

    panel.querySelectorAll('[data-header-menu-root-image]').forEach(image => {
      image.hidden = true;
    });

    panel.querySelectorAll('[data-header-menu-submenu]').forEach(submenu => {
      submenu.hidden = !showSubmenu || submenu.dataset.headerMenuSubmenu !== name;
      if (!submenu.hidden) this._updateHeaderMenuScrollbar(submenu);
    });

    requestAnimationFrame(() => this._positionHeaderMenu());
  }

  _showHeaderMenuRoot() {
    const panel = this._headerMenuPanel;
    if (!panel) return;

    const root = panel.querySelector('[data-header-menu-root]');
    if (root) {
      root.hidden = false;
      requestAnimationFrame(() => this._updateHeaderMenuScrollbar(root));
    }

    panel.querySelectorAll('[data-header-menu-item]').forEach(item => {
      item.classList.remove(this._config.activeClass);
      item.setAttribute('aria-expanded', 'false');
    });

    panel.querySelectorAll('[data-header-menu-root-image]').forEach(image => {
      image.hidden = true;
    });

    panel.querySelectorAll('[data-header-menu-submenu]').forEach(submenu => {
      submenu.hidden = true;
      submenu.classList.remove('is-scrollable');
    });

    requestAnimationFrame(() => this._positionHeaderMenu());
  }

  _closeHeaderMenu() {
    const panel = this._headerMenuPanel;
    if (!panel) return;

    panel.classList.remove(this._config.openClass);
    panel.setAttribute('aria-hidden', 'true');
    this._headerMenuToggle?.setAttribute('aria-expanded', 'false');

    panel.querySelectorAll('[data-header-menu-item]').forEach(item => {
      item.classList.remove(this._config.activeClass);
      item.setAttribute('aria-expanded', 'false');
    });

    this._showHeaderMenuRoot();
    panel.querySelectorAll('[data-header-menu-root-image]').forEach(image => {
      image.hidden = true;
    });

    panel.querySelectorAll('[data-header-menu-submenu]').forEach(submenu => {
      submenu.hidden = true;
    });

    panel.style.left = '';
    panel.style.maxWidth = '';
  }

  _positionHeaderMenu() {
    const panel = this._headerMenuPanel;
    const header = document.querySelector(this._config.containerSelector);
    if (!panel || !header || !panel.classList.contains(this._config.openClass)) return;

    const headerRect = header.getBoundingClientRect();
    const gutter = 12;
    const viewportLeft = gutter;
    const viewportRight = window.innerWidth - gutter;
    const availableWidth = Math.max(
      0,
      Math.min(headerRect.width, viewportRight - viewportLeft)
    );

    panel.style.left = '0px';
    panel.style.right = 'auto';
    panel.style.maxWidth = `${availableWidth}px`;

    const panelRect = panel.getBoundingClientRect();
    const headerLeft = Math.max(viewportLeft, headerRect.left);
    const headerRight = Math.min(viewportRight, headerRect.right);
    let left = headerLeft;

    if (left + panelRect.width > headerRight) {
      left = headerRight - panelRect.width;
    }

    left = Math.max(viewportLeft, Math.min(left, viewportRight - panelRect.width));
    const containingBlockLeft = headerRect.left + header.clientLeft;
    panel.style.left = `${Math.round(left - containingBlockLeft)}px`;

  }

  _onMobileMenuClick(event) {
    const openButton = event.target.closest('[data-mobile-menu-open]');
    if (openButton) {
      this._showMobileMenuPanel(openButton.dataset.mobileMenuOpen);
      return;
    }

    const backButton = event.target.closest('[data-mobile-menu-back]');
    if (backButton) this._showMobileMenuPanel('root');
  }

  _showMobileMenuPanel(panelName) {
    const drawer = this._mobileMenuDrawer;
    if (!drawer) return;

    drawer.querySelectorAll('[data-mobile-menu-panel]').forEach(panel => {
      const isActive = panel.dataset.mobileMenuPanel === panelName;
      panel.hidden = !isActive;
      panel.classList.toggle('is-active', isActive);
    });

    const activePanel = drawer.querySelector(`[data-mobile-menu-panel="${panelName}"]`);
    activePanel?.querySelector('a[href], button:not([data-mobile-menu-back])')?.focus({ preventScroll: true });
  }

  _bindGlobalEvents() {
    document.addEventListener('keydown', this._onKeyDown);
    document.addEventListener('click',   this._onClick);
    window.addEventListener('resize', this._onResize);
    window.addEventListener('scroll', this._onScroll, { passive: true });
  }

  // ─── Eventos globais ──────────────────────────────────────────────────────

  _onKeyDown(e) {
    if (e.key === 'Escape') {
      this.closeDropdowns();
      this._headerMenuToggle?.focus({ preventScroll: true });
    }
  }

  _onClick(e) {
    if (
      e.target.closest('[data-header-menu-toggle]') ||
      e.target.closest('[data-header-menu-panel]')
    ) return;

    // Fecha ao clicar fora de triggers e menus
    if (
      !e.target.closest(this._config.triggerSelector) &&
      !e.target.closest(this._config.menuSelector)
    ) {
      this.closeDropdowns();
    }
  }

  // ─── Timers de hover ──────────────────────────────────────────────────────

  _scheduleOpen(name) {
    this._cancelClose(name);
    this._cancelOpen(name);
    const timer = setTimeout(() => this._open(name), this._config.openDelay);
    this._timers.set(`open:${name}`, timer);
  }

  _scheduleClose(name) {
    this._cancelOpen(name);
    const timer = setTimeout(() => this._close(name), this._config.closeDelay);
    this._timers.set(`close:${name}`, timer);
  }

  _cancelOpen(name) {
    clearTimeout(this._timers.get(`open:${name}`));
    this._timers.delete(`open:${name}`);
  }

  _cancelClose(name) {
    clearTimeout(this._timers.get(`close:${name}`));
    this._timers.delete(`close:${name}`);
  }

  // ─── Abrir / fechar ───────────────────────────────────────────────────────

  _open(name) {
    if (this._activeMenu && this._activeMenu !== name) {
      this._close(this._activeMenu);
    }

    const menu    = document.querySelector(`[data-dropdown-menu="${name}"]`);
    const trigger = document.querySelector(`[data-dropdown-trigger="${name}"]`);
    if (!menu) return;

    menu.classList.add(this._config.openClass);
    menu.setAttribute('aria-hidden', 'false');
    trigger?.classList.add(this._config.activeClass);
    trigger?.setAttribute('aria-expanded', 'true');
    this._activeMenu = name;

    requestAnimationFrame(() => this._positionMenu(menu, trigger));

    window.eventBus?.emit('dropdown:open', { name, menu, trigger });
  }

  _close(name) {
    const menu    = document.querySelector(`[data-dropdown-menu="${name}"]`);
    const trigger = document.querySelector(`[data-dropdown-trigger="${name}"]`);
    if (!menu) return;

    menu.classList.remove(this._config.openClass);
    menu.setAttribute('aria-hidden', 'true');
    menu.style.left = '';
    trigger?.classList.remove(this._config.activeClass);
    trigger?.setAttribute('aria-expanded', 'false');

    if (this._activeMenu === name) this._activeMenu = null;

    window.eventBus?.emit('dropdown:close', { name, menu });
  }

  _positionMenu(menu, trigger) {
    const container = document.querySelector(this._config.containerSelector);
    if (!container || !menu || !trigger || !menu.classList.contains(this._config.openClass)) return;

    const containerRect = container.getBoundingClientRect();
    const triggerRect = trigger.getBoundingClientRect();
    const menuRect = menu.getBoundingClientRect();
    const gutter = 12;
    const viewportLeft = Math.max(gutter, containerRect.left + gutter);
    const viewportRight = Math.min(window.innerWidth - gutter, containerRect.right - gutter);
    const menuWidth = menuRect.width;

    if (!menuWidth) return;

    let left = triggerRect.left - containerRect.left;
    const triggerRight = triggerRect.right - containerRect.left;
    const maxLeft = viewportRight - containerRect.left - menuWidth;
    const minLeft = viewportLeft - containerRect.left;

    // Se abrir à direita ultrapassar a viewport, ancora a borda direita do
    // painel à borda direita do item que está em hover.
    if (triggerRect.right + menuWidth - triggerRect.width > viewportRight) {
      left = triggerRight - menuWidth;
    }

    left = Math.max(minLeft, Math.min(left, maxLeft));
    menu.style.left = `${Math.round(left)}px`;
  }

  _repositionActiveMenu() {
    if (!this._activeMenu) return;

    const menu = document.querySelector(`[data-dropdown-menu="${this._activeMenu}"]`);
    const trigger = document.querySelector(`[data-dropdown-trigger="${this._activeMenu}"]`);
    this._positionMenu(menu, trigger);
  }

  _onResize() {
    if (window.matchMedia?.('(max-width: 749px)').matches) {
      this._closeHeaderMenu();
    }

    this._repositionActiveMenu();
    this._positionHeaderMenu();
    this._headerMenuScrollContainers?.forEach(scrollContainer => {
      if (!scrollContainer.hidden) this._updateHeaderMenuScrollbar(scrollContainer);
    });
  }

  _onScroll() {
    this._repositionActiveMenu();
  }

  // ─── API pública ──────────────────────────────────────────────────────────

  closeDropdowns() {
    if (this._activeMenu) this._close(this._activeMenu);
    // O menu desktop já está fechado durante a abertura do drawer mobile.
    // Evita percorrer todos os itens ocultos como parte do drawer:open.
    if (this._headerMenuPanel?.classList.contains(this._config.openClass)) {
      this._closeHeaderMenu();
    }
  }

  destroy() {
    document.removeEventListener('keydown', this._onKeyDown);
    document.removeEventListener('click',   this._onClick);
    window.removeEventListener('resize', this._onResize);
    window.removeEventListener('scroll', this._onScroll);

    this._mobileMenuDrawer?.removeEventListener('click', this._onMobileMenuClick);
    this._headerMenuToggle?.removeEventListener('click', this._onHeaderMenuToggleClick);
    this._headerMenuPanel?.removeEventListener('click', this._onHeaderMenuClick);
    this._headerMenuScrollContainers?.forEach(scrollContainer => {
      scrollContainer.removeEventListener('scroll', this._onHeaderMenuScroll);
    });

    this._timers.forEach(t => clearTimeout(t));
    this._timers.clear();
  }
}

window.HeaderManager = new HeaderManager();
